const ctx = () => window.XG?.pdp || null;
const rootOf = () => ctx()?.root || null;
const panelOf = () => ctx()?.panel || null;
const isProductRoute = () =>
  rootOf()?.getAttribute('data-xg-pdp-mode') === 'product';
function urlFor(sku) {
  if (isProductRoute()) return sku.url ? new URL(sku.url, location.href) : null;
  const url = new URL(location.href);
  url.searchParams.set('strip', sku.id);
  return url;
}
const resolve = (key) => ctx()?.byId[key]?.id || null;
const wantsMotion = () =>
  window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
const canTransition = () =>
  typeof document.startViewTransition === 'function' && wantsMotion();
const warmed = new Map();
function warm(sku) {
  const api = ctx();
  const lead = sku?.imgs?.[0];
  if (!api || !lead) return null;
  if (warmed.has(sku.id)) return warmed.get(sku.id);
  const img = new Image();
  img.srcset = api.srcsetFor(lead);
  img.sizes = api.sizes;
  img.src = lead.b + 'width=1000';
  const ready = img.decode().catch(() => {}).then(() => img);
  warmed.set(sku.id, ready);
  return ready;
}
const withTimeout = (promise, ms) =>
  Promise.race([promise, new Promise((r) => setTimeout(r, ms))]);
function announce(sku) {
  const status = rootOf()?.querySelector('[data-xg-pdp-status]');
  if (!status) return;
  const note = [sku.note, sku.haz].map((s) => (s || '').trim())
                                  .filter(Boolean).join(' ');
  status.textContent = note
    ? `Now showing ${sku.name}. Safety information: ${note}`
    : `Now showing ${sku.name}.`;
}
let titleTail = null;
function readTitleTail() {
  const name = ctx()?.byId[rootOf()?.getAttribute('data-xg-active')]?.name;
  const at = name ? document.title.indexOf(name) : -1;
  titleTail = at < 0 ? '' : document.title.slice(at + name.length);
}
function syncCanonical(sku) {
  if (!isProductRoute() || !sku.url) return;
  const href = new URL(sku.url, location.href).href;
  const link = document.querySelector('link[rel="canonical"]');
  if (link) link.setAttribute('href', href);
  if (titleTail === null) readTitleTail();
  document.title = sku.name + titleTail;
  for (const node of document.querySelectorAll('script[type="application/ld+json"]')) {
    if (node.textContent.includes('"Product"')) node.remove();
  }
}
function syncPairs(sku) {
  const rail = document.querySelector('[data-xg-pairs="pdp"]');
  if (!rail) return;
  const handle = (sku?.url || '').split('/').pop();
  let on = 0;
  for (const group of rail.querySelectorAll('[data-xg-pair-for]')) {
    const match = !!handle && group.getAttribute('data-xg-pair-for') === handle;
    group.hidden = !match;
    if (match) on += 1;
  }
  rail.hidden = on === 0;
}
const HINT_KEY = 'xg-pdp-hint';
function stopHint() {
  const rail = rootOf()?.querySelector('.xg-pdp-rail');
  if (!rail || !rail.hasAttribute('data-xg-hint')) return;
  rail.removeAttribute('data-xg-hint');
  try { sessionStorage.setItem(HINT_KEY, '1'); } catch {   }
}
function centreChip(id, smooth) {
  const root = rootOf();
  const list = root?.querySelector('.xg-pdp-rail__list');
  const chip = root?.querySelector(`.xg-pdp-rail__chip[data-xg-sku="${CSS.escape(id)}"]`);
  if (!chip || !list || list.scrollWidth <= list.clientWidth) return;
  list.scrollTo({
    left: Math.max(0, chip.offsetLeft - (list.clientWidth - chip.offsetWidth) / 2),
    behavior: smooth && wantsMotion() ? 'smooth' : 'auto',
  });
}
const railOpts = () => [...(rootOf()?.querySelectorAll('.xg-pdp-rail__chip') || [])];
function syncRail() {
  for (const opt of railOpts()) {
    const on = opt.hasAttribute('aria-current');
    opt.setAttribute('role', 'radio');
    opt.setAttribute('aria-checked', on ? 'true' : 'false');
    opt.tabIndex = on ? 0 : -1;
  }
}
function upgradeRail() {
  const list = rootOf()?.querySelector('.xg-pdp-rail__list');
  if (!list) return;
  list.setAttribute('role', 'radiogroup');
  list.setAttribute('aria-labelledby', 'xg-pdp-rail-legend');
  for (const item of list.children) item.setAttribute('role', 'presentation');
  const on = list.querySelector('.xg-pdp-rail__chip[aria-current]');
  if (on) on.setAttribute('aria-current', 'true');
  syncRail();
}
function fallbackSwap(commit) {
  const panel = panelOf();
  if (!wantsMotion()) return commit();
  panel.setAttribute('data-xg-swap', 'out');
  return new Promise((resolve) => {
    const done = () => {
      commit();
      panel.setAttribute('data-xg-swap', 'in');
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          panel.removeAttribute('data-xg-swap');
          resolve();
        })
      );
    };
    panel.addEventListener('transitionend', done, { once: true });
    setTimeout(() => {
      panel.removeEventListener('transitionend', done);
      done();
    }, 280);
  });
}
function markPending(id, on) {
  const root = rootOf();
  const list = root?.querySelector('.xg-pdp-rail__list');
  if (list) list.setAttribute('aria-busy', on ? 'true' : 'false');
  const chip = root?.querySelector(`.xg-pdp-rail__chip[data-xg-sku="${CSS.escape(id)}"]`);
  if (!chip) return;
  if (on) chip.setAttribute('data-xg-state', 'pending');
  else chip.removeAttribute('data-xg-state');
}
let swapping = false;
let pending = null;
let cancelPush = false;
async function select(id, { push = true } = {}) {
  const api = ctx();
  const root = rootOf();
  const sku = api?.byId[id];
  if (!sku || !root) return;
  stopHint();
  if (swapping) {
    pending = { id: sku.id, push };
    if (!push) cancelPush = true;
    return;
  }
  if (root.getAttribute('data-xg-active') === sku.id) return;
  id = sku.id;
  swapping = true;
  markPending(id, true);
  try {
    const warming = warm(sku);
    if (warming) await withTimeout(warming, 240);
    const commit = () => {
      api.apply(id);
      syncRail();
      syncPairs(sku);
      centreChip(id, false);
      announce(sku);
      const url = push && !cancelPush ? urlFor(sku) : null;
      if (url) history.pushState({ xgSku: sku.id }, '', url);
      syncCanonical(sku);
      document.dispatchEvent(new CustomEvent('xg:pdp:select', { detail: { sku: sku.id } }));
    };
    if (canTransition()) {
      document.documentElement.setAttribute('data-xg-vt', 'pdp');
      const transition = document.startViewTransition(commit);
      try {
        await transition.finished;
      } catch {
      }
      document.documentElement.removeAttribute('data-xg-vt');
    } else {
      await fallbackSwap(commit);
      centreChip(id, true);
    }
  } finally {
    markPending(id, false);
    swapping = false;
    cancelPush = false;
    if (pending) {
      const next = pending;
      pending = null;
      select(next.id, { push: next.push });
    }
  }
}
function railChip(target) {
  const link = target?.closest?.('.xg-pdp-rail__chip');
  if (!link || !ctx() || link.closest('[data-xg-pdp]') !== rootOf()) return null;
  return resolve(link.getAttribute('data-xg-sku')) ? link : null;
}
document.addEventListener('click', (event) => {
  const link = railChip(event.target);
  if (!link) return;
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  select(link.getAttribute('data-xg-sku'));
});
const STEP = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
document.addEventListener('keydown', (event) => {
  const link = railChip(event.target);
  if (!link) return;
  if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
  const all = railOpts();
  let next = null;
  if (event.key in STEP) {
    next = all[(all.indexOf(link) + STEP[event.key] + all.length) % all.length];
  } else if (event.key === 'Home') next = all[0];
  else if (event.key === 'End') next = all[all.length - 1];
  else if (event.key === ' ') next = link;
  else return;
  event.preventDefault();
  next.focus();
  select(next.getAttribute('data-xg-sku'));
});
const intent = (event) => {
  const link = railChip(event.target);
  if (link) warm(ctx()?.byId[link.getAttribute('data-xg-sku')]);
};
document.addEventListener('pointerenter', intent, { capture: true });
document.addEventListener('focusin', intent);
document.addEventListener('touchstart', intent, { passive: true });
const usedRail = (event) => {
  if (event.target?.closest?.('.xg-pdp-rail')) stopHint();
};
document.addEventListener('pointerdown', usedRail, { passive: true });
document.addEventListener('keydown', usedRail);
document.addEventListener('scroll', (event) => {
  if (event.target?.classList?.contains('xg-pdp-rail__list')) stopHint();
}, { capture: true, passive: true });
window.addEventListener('popstate', () => {
  const root = rootOf();
  if (!root) return;
  const key = isProductRoute()
    ? location.pathname.replace(/\/+$/, '').split('/').pop()
    : new URLSearchParams(location.search).get('strip');
  const id = resolve(key)
    || resolve(root.getAttribute('data-xg-default'))
    || ctx()?.data[0]?.id;
  if (id) select(id, { push: false });
});
document.addEventListener('click', (event) => {
  const api = ctx();
  const thumb = event.target.closest?.('[data-xg-pdp-thumb]');
  if (!api || !thumb || !panelOf()?.contains(thumb)) return;
  api.photo(api.byId[rootOf().getAttribute('data-xg-active')],
            parseInt(thumb.getAttribute('data-xg-pdp-thumb'), 10) || 0);
});
document.addEventListener('change', (event) => {
  const api = ctx();
  const input = event.target;
  if (!api || !panelOf()?.contains(input)) return;
  if (input.name !== 'xg-pdp-pack' && input.name !== 'selling_plan') return;
  api.reprice();
});
document.addEventListener('submit', (event) => {
  const form = event.target?.closest?.('[data-xg-pdp-form]');
  if (!form) return;
  const checked = form.querySelector('input[name="selling_plan"]:checked');
  if (checked && checked.value === '') {
    checked.disabled = true;
    setTimeout(() => { checked.disabled = false; }, 0);
  }
}, { capture: true });
function init() {
  const root = rootOf();
  if (!root) return;
  upgradeRail();
  syncPairs(ctx()?.byId[root.getAttribute('data-xg-active')]);
  centreChip(root.getAttribute('data-xg-active'), false);
  if (titleTail === null) readTitleTail();
  let seen = false;
  try { seen = sessionStorage.getItem(HINT_KEY) === '1'; } catch {   }
  if (seen) stopHint();
  root.setAttribute('data-xg-pdp-ready', '');
}
function frugal() {
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!c) return false;
  if (c.saveData) return true;
  const t = c.effectiveType || '';
  return t === 'slow-2g' || t === '2g' || t === '3g';
}
window.addEventListener('load', () => {
  if (frugal()) return;
  const run = () => {
    const api = ctx();
    const active = rootOf()?.getAttribute('data-xg-active');
    if (!api || !active) return;
    const i = api.data.findIndex((s) => s.id === active);
    if (i < 0) return;
    warm(api.data[i + 1]);
    warm(api.data[i - 1]);
  };
  if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 3000 });
  else setTimeout(run, 1200);
}, { once: true });
document.addEventListener('shopify:section:load', init);
init();
